import type { MenuItem } from "../shared/components/Sidenav";
import { RESERVED_TOP_LEVEL_SEGMENTS, type PluginRoute } from "../shared/services/pluginService";

const trimSlashes = (path: string) => path.replace(/^\/+|\/+$/g, "");

/** Expands a route tree to the absolute paths it mounts (index routes render at the parent path). */
const collectRoutePaths = (routes: PluginRoute[], parentPath: string, into: string[]) => {
    for (const route of routes) {
        const path = route.index || !route.path ? parentPath : `${parentPath}/${trimSlashes(route.path)}`;
        into.push(path || "/");
        if (route.children)
            collectRoutePaths(route.children, path, into);
    }
};

/** Matches a concrete URL against a route pattern (":param" matches one segment, "*" the rest). */
const matchesRoutePattern = (url: string, pattern: string): boolean => {
    const urlSegments = url.split("/").filter(Boolean);
    const patternSegments = pattern.split("/").filter(Boolean);

    for (let i = 0; i < patternSegments.length; i++) {
        const segment = patternSegments[i];
        if (segment === "*")
            return true;
        if (i >= urlSegments.length)
            return false;
        if (!segment.startsWith(":") && segment !== urlSegments[i])
            return false;
    }

    return urlSegments.length === patternSegments.length;
};

/**
 * Checks a plugin's `routes` and `menuItems` registrations for the mistakes the host only surfaces
 * at runtime (or not at all): reserved/invalid top-level route paths, duplicate menu item names,
 * and menu URLs that don't resolve to any registered route.
 *
 * Menu URLs pointing outside the plugin's own route subtrees (e.g. built-in storyline pages or
 * another plugin) are not validated - only this plugin's registrations are known here.
 *
 * Returns a list of problems; an empty list means the module is valid. Intended for a plugin's
 * test suite (see `expectValidPluginRoutes` in `@qerent/lib/test/helpers`) or a fail-fast call
 * during development.
 */
export const validatePluginModule = (routes: PluginRoute[], menuItems: MenuItem[]): string[] => {
    const problems: string[] = [];

    for (const route of routes) {
        const path = route.path?.replace(/^\/+/, "");

        if (!path || route.index) {
            problems.push("top-level routes require a non-empty path (index routes are reserved for the home page)");
            continue;
        }

        const firstSegment = path.split("/")[0];
        if (firstSegment === "*" || RESERVED_TOP_LEVEL_SEGMENTS.includes(firstSegment))
            problems.push(`route path '${path}' conflicts with a built-in route and would be skipped by the host`);
    }

    const routePaths: string[] = [];
    collectRoutePaths(routes, "", routePaths);
    const ownTopLevelSegments = new Set(routePaths.map(path => path.split("/").filter(Boolean)[0]).filter(Boolean));

    const seenNames = new Set<string>();
    const visitMenuItems = (items: MenuItem[]) => {
        for (const item of items) {
            if (item.name) {
                if (seenNames.has(item.name))
                    problems.push(`menu item name '${item.name}' is not unique`);
                seenNames.add(item.name);
            }

            if (item.url) {
                const target = item.url.split(/[?#]/)[0];
                const firstSegment = target.split("/").filter(Boolean)[0];

                if (firstSegment && ownTopLevelSegments.has(firstSegment) && !routePaths.some(pattern => matchesRoutePattern(target, pattern)))
                    problems.push(`menu item '${item.name ?? item.title ?? target}' links to '${item.url}', which does not match any registered route`);
            }

            if (item.children)
                visitMenuItems(item.children);
        }
    };
    visitMenuItems(menuItems);

    return problems;
};
